import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import CodeIcon from "@mui/icons-material/Code";
import ArticleIcon from "@mui/icons-material/Article";
import ReactMarkdown from "react-markdown";
import { encodeKey, FileItem } from "./FileGrid";

interface FilePreviewDrawerProps {
  file: FileItem | null;
  onClose: () => void;
  onEdit?: (file: FileItem) => void;
}

function extractFilename(key: string) {
  return key.split("/").pop() || key;
}

function isMarkdown(contentType: string, filename: string) {
  return (
    contentType === "text/markdown" ||
    contentType === "text/x-markdown" ||
    filename.endsWith(".md") ||
    filename.endsWith(".markdown")
  );
}

function isTextFile(contentType: string, filename: string) {
  return (
    contentType.startsWith("text/") ||
    contentType === "application/json" ||
    isMarkdown(contentType, filename)
  );
}

const FilePreviewDrawer: React.FC<FilePreviewDrawerProps> = ({
  file,
  onClose,
  onEdit,
}) => {
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [markdownView, setMarkdownView] = useState<"rendered" | "raw">("rendered");

  const fileUrl = file ? `/webdav/${encodeKey(file.key)}` : "";
  const contentType = file?.httpMetadata?.contentType || "";

  const filename = file ? extractFilename(file.key) : "";
  const isMarkdownFile = isMarkdown(contentType, filename);
  const isText = isTextFile(contentType, filename);

  useEffect(() => {
    if (!file) {
      setTextContent(null);
      setMarkdownView("rendered");
      return;
    }

    if (contentType.startsWith("text/") || contentType === "application/json") {
      setLoading(true);
      fetch(fileUrl)
        .then((res) => res.text())
        .then((text) => setTextContent(text))
        .catch(() => setTextContent("Failed to load content"))
        .finally(() => setLoading(false));
    } else {
      setTextContent(null);
    }
  }, [file, fileUrl, contentType]);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = extractFilename(file!.key);
    a.click();
  };

  const handleOpenInNewTab = () => {
    window.open(fileUrl, "_blank", "noopener,noreferrer");
  };

  const renderPreview = () => {
    if (!file) return null;

    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (contentType.startsWith("image/")) {
      return (
        <Box sx={{ textAlign: "center" }}>
          <img
            src={fileUrl}
            alt={file.key}
            style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
          />
        </Box>
      );
    }

    if (contentType.startsWith("video/")) {
      return (
        <video controls style={{ width: "100%", maxHeight: "70vh" }}>
          <source src={fileUrl} type={contentType} />
        </video>
      );
    }

    if (contentType.startsWith("audio/")) {
      return (
        <audio controls style={{ width: "100%" }}>
          <source src={fileUrl} type={contentType} />
        </audio>
      );
    }

    if (contentType === "application/pdf") {
      return (
        <iframe
          src={fileUrl}
          style={{ width: "100%", height: "70vh", border: "none" }}
          title={file.key}
        />
      );
    }

    if (textContent !== null) {
      if (isMarkdownFile && markdownView === "rendered") {
        return (
          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.mode === "dark" ? "grey.900" : "grey.100",
              p: 2,
              borderRadius: 1,
              maxHeight: "70vh",
              overflow: "auto",
              "& h1, & h2, & h3, & h4, & h5, & h6": { mt: 2, mb: 1 },
              "& p": { my: 1 },
              "& pre": {
                backgroundColor: theme.palette.mode === "dark" ? "grey.800" : "grey.200",
                p: 1,
                borderRadius: 1,
                overflow: "auto",
              },
              "& code": {
                backgroundColor: theme.palette.mode === "dark" ? "grey.800" : "grey.200",
                px: 0.5,
                borderRadius: 0.5,
                fontFamily: "monospace",
              },
              "& pre code": { backgroundColor: "transparent", p: 0 },
              "& blockquote": {
                borderLeft: 4,
                borderColor: "grey.400",
                pl: 2,
                ml: 0,
                color: "text.secondary",
              },
              "& ul, & ol": { pl: 3 },
              "& a": { color: "primary.main" },
            })}
          >
            <ReactMarkdown>{textContent}</ReactMarkdown>
          </Box>
        );
      }
      return (
        <Box
          component="pre"
          sx={(theme) => ({
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            backgroundColor: theme.palette.mode === "dark" ? "grey.900" : "grey.100",
            p: 2,
            borderRadius: 1,
            maxHeight: "70vh",
            overflow: "auto",
            fontFamily: "monospace",
            fontSize: "0.875rem",
          })}
        >
          {textContent}
        </Box>
      );
    }

    return (
      <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
        Preview not available for this file type
      </Typography>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={file !== null}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 500, md: 600 } } }}
    >
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mr: 1, flexShrink: 1 }}
          >
            {file ? extractFilename(file.key) : ""}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            {isText && textContent !== null && onEdit && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => onEdit(file!)}
                  sx={{ color: "text.primary" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isMarkdownFile && textContent !== null && (
              <ToggleButtonGroup
                size="small"
                value={markdownView}
                exclusive
                onChange={(_, value) => value && setMarkdownView(value)}
              >
                <ToggleButton value="rendered" title="Rendered">
                  <ArticleIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="raw" title="Raw">
                  <CodeIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: "auto", mb: 2 }}>
          {renderPreview()}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            fullWidth
          >
            Download
          </Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNewTab}
            fullWidth
          >
            Open
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilePreviewDrawer;
